import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQuery } from 'convex/react'
import toast from 'react-hot-toast'
import {
  Plus,
  Search,
  FolderOpen,
  Clock,
  MoreHorizontal,
  Trash2,
  TreeDeciduous,
  CheckCircle2,
  RotateCcw,
  Loader2,
} from 'lucide-react'
import {
  Button,
  Card,
  Input,
  Modal,
  Badge,
  Skeleton,
} from '@/components/common'
import { PageContainer, PageHeader, EmptyState } from '@/components/layout'
import { formatRelativeTime } from '@/utils/formatters'
import { useAuthStore } from '@/stores/authStore'
import type { Project, ProjectStatus } from '@/types'
// Convex generated api lives outside `src`; use a relative path.
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

/**
 * Normalize a Convex `projects` document to the UI-side `Project` shape.
 * Keeps the component agnostic to Convex-internal fields like
 * `_creationTime` and the full `problemOutput` sub-doc.
 */
function projectFromConvex(doc: Record<string, unknown>): Project {
  return {
    _id: String(doc._id),
    userId: String(doc.userId),
    name: (doc.name as string) ?? 'Untitled',
    description: (doc.description as string | undefined) ?? undefined,
    tags: (doc.tags as string[] | undefined) ?? undefined,
    status: (doc.status as ProjectStatus) ?? 'draft',
    initialSituation: (doc.initialSituation as string | undefined) ?? undefined,
    cleanedSituation: (doc.cleanedSituation as string | undefined) ?? undefined,
    createdAt: Number(doc.createdAt ?? doc._creationTime ?? Date.now()),
    updatedAt: Number(doc.updatedAt ?? doc._creationTime ?? Date.now()),
    completedAt: doc.completedAt ? Number(doc.completedAt) : undefined,
    currentRound: Number(doc.currentRound ?? 0),
    totalNodes: Number(doc.totalNodes ?? 0),
    activeBranches: Number(doc.activeBranches ?? 0),
    generationInProgress:
      typeof doc.generationInProgress === 'string'
        ? doc.generationInProgress
        : undefined,
  }
}

const ProjectsPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const convexUserId = user?.convexUserId

  const createProject = useMutation(api.projects.create)
  const updateProject = useMutation(api.projects.update)
  const removeProject = useMutation(api.projects.remove)

  // useQuery returns `undefined` while loading and `null`/`[]` once resolved.
  const convexProjects = useQuery(
    api.projects.listByUser,
    convexUserId
      ? { userId: convexUserId as unknown as Id<'users'> }
      : 'skip',
  )

  const projects = useMemo<Project[]>(
    () => (convexProjects ?? []).map((p) => projectFromConvex(p as Record<string, unknown>)),
    [convexProjects],
  )

  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const isLoading = !!convexUserId && convexProjects === undefined

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase())),
  )

  const activeProjects = filteredProjects
  const archivedProjects: Project[] = []

  const handleToggleComplete = async (project: Project) => {
    const nextStatus: ProjectStatus =
      project.status === 'completed' ? 'in_progress' : 'completed'
    try {
      await updateProject({
        projectId: project._id as unknown as Id<'projects'>,
        status: nextStatus,
      })
      toast.success(
        nextStatus === 'completed'
          ? `Marked "${project.name}" as complete`
          : `Moved "${project.name}" back to in-progress`,
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed')
    }
  }

  const handleDelete = async (project: Project) => {
    const confirmed = window.confirm(
      `Delete "${project.name}"? This cannot be undone.`,
    )
    if (!confirmed) return
    try {
      await removeProject({
        projectId: project._id as unknown as Id<'projects'>,
      })
      toast.success(`Deleted "${project.name}"`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  const handleCreate = async (input: {
    name: string
    description?: string
    tags?: string[]
  }): Promise<Project | null> => {
    if (!convexUserId) {
      toast.error('Not connected to Convex. Sign out and back in to retry.')
      return null
    }
    try {
      const newId = await createProject({
        userId: convexUserId as unknown as Id<'users'>,
        name: input.name,
        description: input.description,
        tags: input.tags,
      })
      // Optimistically construct the project object. The real-time query
      // will replace this shortly, but the caller navigates straight away.
      const now = Date.now()
      return {
        _id: String(newId),
        userId: convexUserId,
        name: input.name,
        description: input.description,
        tags: input.tags,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
        currentRound: 0,
        totalNodes: 0,
        activeBranches: 0,
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Create failed')
      return null
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Your Projects"
        description="Design and explore problems through curiosity-driven inquiry"
        actions={
          <Button
            leftIcon={<Plus className="w-5 h-5" />}
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!convexUserId}
          >
            New Project
          </Button>
        }
      />

      {!convexUserId && (
        <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300">
          Convex isn't connected yet. Sign out and back in, or check your
          <code className="mx-1">VITE_CONVEX_URL</code> and backend logs.
        </div>
      )}

      <div className="mb-8">
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="w-5 h-5" />}
          className="max-w-md"
        />
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6">
              <Skeleton height={24} width="70%" className="mb-3" />
              <Skeleton height={16} className="mb-2" />
              <Skeleton height={16} width="60%" className="mb-4" />
              <div className="flex gap-2">
                <Skeleton height={24} width={60} rounded="full" />
                <Skeleton height={24} width={80} rounded="full" />
              </div>
            </Card>
          ))}
        </div>
      ) : activeProjects.length === 0 && archivedProjects.length === 0 ? (
        <EmptyState
          icon={<FolderOpen className="w-8 h-8" />}
          title="No projects yet"
          description="Create your first project to start designing problems"
          action={
            <Button
              leftIcon={<Plus className="w-5 h-5" />}
              onClick={() => setIsCreateModalOpen(true)}
              disabled={!convexUserId}
            >
              Create Project
            </Button>
          }
        />
      ) : (
        <div className="space-y-8">
          {activeProjects.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Active Projects
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {activeProjects.map((project, index) => (
                    <ProjectCard
                      key={project._id}
                      project={project}
                      index={index}
                      onClick={() => navigate(`/workspace/${project._id}`)}
                      onToggleComplete={handleToggleComplete}
                      onDelete={handleDelete}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {archivedProjects.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-500 dark:text-slate-400 mb-4">
                Archived
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-70">
                {archivedProjects.map((project, index) => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    index={index}
                    onClick={() => navigate(`/workspace/${project._id}`)}
                    onToggleComplete={handleToggleComplete}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={async (input) => {
          const project = await handleCreate(input)
          if (project) {
            setIsCreateModalOpen(false)
            navigate(`/workspace/${project._id}`)
          }
        }}
      />
    </PageContainer>
  )
}

interface ProjectCardProps {
  project: Project
  index: number
  onClick: () => void
  onToggleComplete: (project: Project) => void
  onDelete: (project: Project) => void
}

const ProjectCard = ({
  project,
  index,
  onClick,
  onToggleComplete,
  onDelete,
}: ProjectCardProps) => {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const statusConfig: Record<
    ProjectStatus,
    { label: string; variant: 'blue' | 'mint' | 'yellow' | 'default' }
  > = {
    draft: { label: 'Draft', variant: 'default' },
    in_progress: { label: 'In Progress', variant: 'blue' },
    completed: { label: 'Completed', variant: 'mint' },
  }

  const { label, variant } = statusConfig[project.status]
  const isCompleted = project.status === 'completed'

  const generationLabel: Record<string, string> = {
    cleaner: 'Cleaning…',
    qai: 'Generating questions…',
    oai: 'Synthesizing problem…',
  }
  const generationText = project.generationInProgress
    ? generationLabel[project.generationInProgress] ??
      `${project.generationInProgress} working…`
    : null

  useEffect(() => {
    if (!showMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card className="relative cursor-pointer group" hover onClick={onClick}>
        <div className="absolute top-4 right-4 flex items-center gap-2" ref={menuRef}>
          <Badge variant={variant}>{label}</Badge>
          <button
            aria-label="Project actions"
            onClick={(e) => {
              e.stopPropagation()
              setShowMenu((prev) => !prev)
            }}
            className={`p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-all ${
              showMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <MoreHorizontal className="w-4 h-4 text-slate-500" />
          </button>

          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.12 }}
                className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-20 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onToggleComplete(project)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
                >
                  {isCompleted ? (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Mark as in progress
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Mark as complete
                    </>
                  )}
                </button>
                <div className="h-px bg-slate-100 dark:bg-slate-700" />
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onDelete(project)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete project
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 pr-24">
          {project.name}
        </h3>

        {project.description && (
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
            {project.description}
          </p>
        )}

        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {project.tags.map((tag) => (
              <Badge key={tag} variant="outline" size="sm">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {generationText && (
          <div className="mb-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-light-icy-blue/15 dark:bg-dark-muted-teal/15 text-xs font-medium text-light-icy-blue dark:text-dark-muted-teal">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {generationText}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-4 border-t border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <TreeDeciduous className="w-3.5 h-3.5" />
              {project.totalNodes} nodes
            </span>
            {project.status === 'in_progress' && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-light-icy-blue dark:bg-dark-muted-teal animate-pulse" />
                {project.activeBranches} active
              </span>
            )}
            {project.status === 'completed' && (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Complete
              </span>
            )}
          </div>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatRelativeTime(project.updatedAt)}
          </span>
        </div>
      </Card>
    </motion.div>
  )
}

interface CreateProjectModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (input: {
    name: string
    description?: string
    tags?: string[]
  }) => Promise<void>
}

const CreateProjectModal = ({
  isOpen,
  onClose,
  onCreate,
}: CreateProjectModalProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return

    setIsCreating(true)

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)

    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      })
      setName('')
      setDescription('')
      setTagsInput('')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Project"
      description="Start a new curiosity-driven problem design session"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            isLoading={isCreating}
            disabled={!name.trim()}
          >
            Create Project
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Project Name"
          placeholder="e.g., Traffic Congestion in Bangalore"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isCreating}
        />

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Description (optional)
          </label>
          <textarea
            className="textarea-field"
            placeholder="Brief context about the situation you want to explore..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isCreating}
            rows={3}
          />
        </div>

        <Input
          label="Tags (optional)"
          placeholder="urban, mobility, india (comma-separated)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          hint="Add tags to organize your projects"
          disabled={isCreating}
        />
      </div>
    </Modal>
  )
}

export default ProjectsPage
